import React, { useCallback, useMemo } from 'react';
import { Route, BrowserRouter } from 'react-router-dom';
import { WalletAdapterNetwork, WalletError } from '@solana/wallet-adapter-base';
import { clusterApiUrl } from '@solana/web3.js';
import {
  LedgerWalletAdapter,
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  SolletWalletAdapter,
  GlowWalletAdapter,
  WalletConnectWalletAdapter,
  BackpackWalletAdapter,
  ExodusWalletAdapter,
  TorusWalletAdapter,
  BraveWalletAdapter,
} from '@solana/wallet-adapter-wallets';
import toast, { Toaster } from 'react-hot-toast';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { Notification } from './common/WalletConnection/Notification';
import TokenStatusScreen from './screens/TokenStatusScreen';
import WalletConnectionScreen from './screens/WalletConnectionScreen';
import DocumentationScreen from './screens/DocumentationScreen';
import { getPortalConfig } from './common/config';

const IS_PROD = (process.env.REACT_APP_STAGE || 'prod') === 'prod';

const walletConnectProjectId = '82329781ad11d885787f56261bd891ca';

export default function App(): JSX.Element {
  const network = getPortalConfig().walletAdapterNetwork;
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // @solana/wallet-adapter-wallets imports all the adapters but supports tree shaking --
  // Only the wallets you want to support will be compiled into your application
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new BraveWalletAdapter(),
      new SolflareWalletAdapter(),
      new LedgerWalletAdapter(),
      new BackpackWalletAdapter(),
      new GlowWalletAdapter(),
      new WalletConnectWalletAdapter({
        options: { projectId: walletConnectProjectId },
        network: network as WalletAdapterNetwork.Mainnet | WalletAdapterNetwork.Devnet,
      }),
      new ExodusWalletAdapter(),
      new TorusWalletAdapter(),
      // Non-prod wallets
      ...(IS_PROD ? [] : [new SolletWalletAdapter({ network })]),
    ],
    [network]
  );

  const onError = useCallback(
    (error: WalletError) =>
      toast.custom(
        <Notification message={error.message ? `${error.name}: ${error.message}` : error.name} variant="error" />
      ),
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} onError={onError} autoConnect>
        <WalletModalProvider>
          <BrowserRouter>
            <Route path="/" exact component={WalletConnectionScreen} />
            <Route path="/status" exact component={TokenStatusScreen} />
            <Route path="/documentation" exact component={DocumentationScreen} />
          </BrowserRouter>
        </WalletModalProvider>
        <Toaster position="bottom-left" reverseOrder={false} />
      </WalletProvider>
    </ConnectionProvider>
  );
}
